/*
 * AECP-style metaobject photo directory.
 *
 * Loads every metaobject of a given type from the Storefront API, paginating
 * past Liquid's 50-item cap on list metafields. The same engine powers several
 * independent lists on one page (AECP participants, Educators Level 1/2), so it
 * processes EVERY [data-aecp-participants] root, not just the first. Each root
 * carries its own config in data-* attributes (see
 * snippets/metafield-aecp-participants.liquid). Order is the Storefront API's
 * default (oldest metaobject first).
 */
(function () {
  'use strict';

  var roots = document.querySelectorAll('[data-aecp-participants]');
  if (!roots.length) return;

  var PAGE_SIZE = 250; // Storefront connection maximum.

  var QUERY = [
    'query AecpParticipants($type: String!, $first: Int!, $after: String) {',
    '  metaobjects(type: $type, first: $first, after: $after) {',
    '    pageInfo { hasNextPage endCursor }',
    '    nodes {',
    '      name: field(key: "name") { value }',
    '      description: field(key: "description") { value }',
    '      image: field(key: "image") {',
    '        reference { ... on MediaImage { image { url altText } } }',
    '      }',
    '    }',
    '  }',
    '}'
  ].join('\n');

  // Append &width= to a Shopify CDN url so we request an appropriately sized image.
  function sizedUrl(url, width) {
    if (!url) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function fieldValue(node, key) {
    return (node[key] && node[key].value) || '';
  }

  // Set up one list. Everything that depends on this list's config/DOM lives in
  // the closure so multiple lists on the same page never share state.
  function initList(root) {
    var listEl = root.querySelector('[data-aecp-list]');
    var statusEl = root.querySelector('[data-aecp-status]');

    var config = {
      token: root.dataset.sfToken,
      version: root.dataset.sfVersion || '2025-10',
      type: root.dataset.metaobjectType || 'aecp_participants',
      showName: root.dataset.showName === 'true',
      // 'card' = image + description (2 per row); 'grid' = image-only tiles
      // (4/3/2 per row) with the name always hidden behind the image.
      layout: root.dataset.layout === 'grid' ? 'grid' : 'card'
    };

    function setStatus(message) {
      if (statusEl) statusEl.textContent = message;
    }

    if (!config.token) {
      setStatus('This list is currently unavailable.');
      return;
    }

    function fetchPage(after) {
      return fetch('/api/' + config.version + '/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': config.token
        },
        body: JSON.stringify({
          query: QUERY,
          variables: { type: config.type, first: PAGE_SIZE, after: after || null }
        })
      }).then(function (response) {
        if (!response.ok) {
          throw new Error('Storefront API responded with ' + response.status);
        }
        return response.json();
      }).then(function (payload) {
        if (payload.errors && payload.errors.length) {
          throw new Error(payload.errors[0].message);
        }
        var connection = payload.data && payload.data.metaobjects;
        return connection || { nodes: [], pageInfo: { hasNextPage: false, endCursor: null } };
      });
    }

    function loadAll() {
      var collected = [];

      function next(after) {
        return fetchPage(after).then(function (connection) {
          (connection.nodes || []).forEach(function (node) {
            if (node) collected.push(node);
          });
          if (connection.pageInfo && connection.pageInfo.hasNextPage) {
            return next(connection.pageInfo.endCursor);
          }
          return collected;
        });
      }

      return next(null);
    }

    function buildImage(imageField, name, nameBehind) {
      var media = imageField && imageField.reference && imageField.reference.image;
      var wrapper = document.createElement('div');
      wrapper.className = 'aecp-participant__image-wrapper';
      // When the name is hidden we still render it (behind the image) so it stays in
      // the DOM and remains findable via the browser's Ctrl+F search.
      if (nameBehind) {
        wrapper.className += ' aecp-participant__image-wrapper--name-hidden';
      }

      if (media && media.url) {
        var img = document.createElement('img');
        img.className = 'aecp-participant__image';
        img.loading = 'lazy';
        img.decoding = 'async';
        img.alt = media.altText || name || '';
        if (config.layout === 'grid') {
          // Tiles span ~25vw desktop / 33vw tablet / 50vw mobile — request larger
          // sources so 4-up tiles stay crisp on high-density displays.
          img.src = sizedUrl(media.url, 400);
          img.srcset = sizedUrl(media.url, 300) + ' 300w, ' +
            sizedUrl(media.url, 400) + ' 400w, ' +
            sizedUrl(media.url, 600) + ' 600w, ' +
            sizedUrl(media.url, 800) + ' 800w';
          img.sizes = '(max-width: 480px) 50vw, (max-width: 989px) 33vw, 25vw';
        } else {
          img.src = sizedUrl(media.url, 400);
          img.srcset = sizedUrl(media.url, 180) + ' 180w, ' +
            sizedUrl(media.url, 360) + ' 360w, ' +
            sizedUrl(media.url, 400) + ' 400w';
          img.sizes = '180px';
        }
        wrapper.appendChild(img);
      } else {
        var placeholder = document.createElement('div');
        placeholder.className = 'aecp-participant__image placeholder-svg';
        wrapper.appendChild(placeholder);
      }

      // Grid tiles never render a visible name caption — the name lives on the
      // image's alt attribute only. The card layout keeps the caption element.
      if (name && config.layout !== 'grid') {
        var caption = document.createElement('span');
        caption.className = 'aecp-participant__image-name';
        caption.textContent = name;
        wrapper.appendChild(caption);
      }

      return wrapper;
    }

    // Mirror Liquid's `newline_to_br`: preserve line breaks, escape everything else
    // (textContent handles escaping).
    function buildDescription(text) {
      var container = document.createElement('div');
      container.className = 'aecp-participant__description';

      var lines = (text || '').split('\n');
      lines.forEach(function (line, index) {
        if (index > 0) container.appendChild(document.createElement('br'));
        container.appendChild(document.createTextNode(line));
      });

      return container;
    }

    function buildParticipant(node) {
      var name = fieldValue(node, 'name').trim();

      var card = document.createElement('div');
      card.className = 'aecp-participant';

      // Grid layout: an image-only tile. The name is carried by the image's alt
      // attribute (never a visible caption), and the image is shown uncropped.
      if (config.layout === 'grid') {
        card.appendChild(buildImage(node.image, name, false));
        return card;
      }

      var col1 = document.createElement('div');
      col1.className = 'aecp-participant__col-1';
      col1.appendChild(buildImage(node.image, name, !config.showName));

      if (config.showName && name) {
        var heading = document.createElement('h3');
        heading.className = 'aecp-participant__name';
        heading.textContent = name;
        col1.appendChild(heading);
      }

      var col2 = document.createElement('div');
      col2.className = 'aecp-participant__col-2';
      col2.appendChild(buildDescription(fieldValue(node, 'description')));

      card.appendChild(col1);
      card.appendChild(col2);
      return card;
    }

    function render(nodes) {
      if (!nodes.length) {
        setStatus('Nothing to show here yet.');
        return;
      }

      var fragment = document.createDocumentFragment();
      var seen = {};

      nodes.forEach(function (node) {
        // This is a photo directory: skip entries with no image (e.g. incomplete
        // or placeholder metaobjects) so no broken card shows.
        var hasImage = node.image && node.image.reference && node.image.reference.image;
        if (!hasImage) return;
        var name = fieldValue(node, 'name').trim().toLowerCase();
        if (name && seen[name]) return; // De-dupe by name.
        if (name) seen[name] = true;
        fragment.appendChild(buildParticipant(node));
      });

      listEl.innerHTML = '';
      listEl.appendChild(fragment);
    }

    loadAll()
      .then(render)
      .catch(function (error) {
        if (window.console && console.error) {
          console.error('[AECP list: ' + config.type + ']', error);
        }
        setStatus('Sorry, we couldn’t load this list right now.');
      });
  }

  // Idempotent: the list snippet ships this script tag, so a page with several
  // lists includes it several times. Each run inits only roots not yet handled,
  // so re-running is a no-op.
  Array.prototype.forEach.call(roots, function (root) {
    if (root.dataset.aecpInitialized === 'true') return;
    root.dataset.aecpInitialized = 'true';
    initList(root);
  });
})();
